import React from 'react';
import { motion } from 'framer-motion';
import { Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';

const TeamPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Meet Our Team | CTC Waterworks"
        description="Meet the experienced team behind CTC Waterworks' sulfur burners, hydrocoolers, and filtration systems. Connect with Brandon Haley, Andre Savala, and Jordan Sloan."
        canonicalPath="/team"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-2 bg-ctc-blue/10 text-ctc-blue px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Users size={16} />
              <span>Our People</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Meet the Team
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              At CTC Waterworks, we combine field experience and engineering expertise to help growers
              solve water quality challenges and protect crop performance. Meet the people behind our
              sulfur burners, hydrocoolers, and filtration systems.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Brandon Haley Card */}
            <Link to="/team/brandon">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-2xl p-8 border border-ctc-blue/20 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer h-full flex flex-col"
              >
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-32 h-32 rounded-full overflow-hidden mb-6 border-4 border-white shadow-xl">
                    <img
                      src="/Brandon Haley.png"
                      alt="Brandon Haley"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Brandon Haley</h2>
                  <p className="text-ctc-blue font-semibold text-lg mb-4">CEO - Owner</p>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6 text-center flex-grow">
                  Brandon leads CTC Waterworks with decades of hands-on experience in manufacturing. As owner of the company, he focuses on building reliable systems, fostering strong customer relationships, partnerships, and helping customers see better profits through delivering better water solutions.
                </p>

                <div className="text-center">
                  <div className="inline-flex items-center space-x-2 text-ctc-blue font-semibold">
                    <span>View Contact Page</span>
                    <ArrowRight size={18} />
                  </div>
                </div>
              </motion.div>
            </Link>

            {/* Andre Savala Card */}
            <Link to="/team/andre">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer h-full flex flex-col"
              >
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-32 h-32 rounded-full overflow-hidden mb-6 border-4 border-white shadow-xl">
                    <img
                      src="/Andre Savala.png"
                      alt="Andre Savala"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Andre Savala</h2>
                  <p className="text-green-700 font-semibold text-lg mb-4">Sales & Customer Support</p>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6 text-center flex-grow">
                  Andre is passionate about helping customers find solutions that fit their unique operations.
                  With extensive experience working with growers, he plays a key role in supporting sulfur
                  burner projects, hydrocooler installations, and day-to-day customer conversations across
                  the agricultural equipment industry.
                </p>

                <div className="text-center">
                  <div className="inline-flex items-center space-x-2 text-green-600 font-semibold">
                    <span>View Contact Page</span>
                    <ArrowRight size={18} />
                  </div>
                </div>
              </motion.div>
            </Link>

            {/* Jordan Sloan Card */}
            <Link to="/team/jordan">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border border-pink-200 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer h-full flex flex-col"
              >
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-32 h-32 rounded-full overflow-hidden mb-6 border-4 border-white shadow-xl">
                    <img
                      src="/Jordan Sloan.png"
                      alt="Jordan Sloan"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Jordan Sloan</h2>
                  <p className="text-pink-600 font-semibold text-lg mb-4">Customer Experience & Outreach</p>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6 text-center flex-grow">
                  Jordan plays a key role in connecting CTC Waterworks with growers, packers, and operators in the field. She focuses on listening to customers, understanding their challenges, and turning their feedback into clear, helpful communication. Through thoughtful outreach and content, Jordan helps make every interaction with CTC a positive one.
                </p>

                <div className="text-center">
                  <div className="inline-flex items-center space-x-2 text-pink-600 font-semibold">
                    <span>View Contact Page</span>
                    <ArrowRight size={18} />
                  </div>
                </div>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* Cross-links Section */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Want to Learn More About CTC?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link
                to="/about"
                className="bg-white border-2 border-ctc-blue text-ctc-blue px-8 py-6 rounded-xl font-semibold hover:bg-ctc-blue hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md group"
              >
                <span className="text-lg">About CTC Waterworks</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link
                to="/contact"
                className="bg-white border-2 border-green-600 text-green-600 px-8 py-6 rounded-xl font-semibold hover:bg-green-600 hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md group"
              >
                <span className="text-lg">Contact Us</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamPage;
